// src/admin/AdminHeroSlides.jsx
import { useEffect, useState, useRef } from "react";
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, updateDoc } from "../services/mysqlStore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, storage } from "../services/mysqlStorage";
import { db } from "../services/mysqlStore";

const EMPTY = { headline: "", subheading: "", ctaLabel: "Shop Now", ctaLink: "/products", imageUrl: "", bgColor: "#0d0d1a" };

// ── Reusable image uploader (same as in AdminProducts) ───────────────────────
function ImageUploader({ value, onChange, folder = "hero" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    setUploading(true);
    task.on(
      "state_changed",
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => { console.error(err); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onChange(url);
        setUploading(false);
        setProgress(0);
      }
    );
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (value) {
      try { await deleteObject(ref(storage, value)); } catch {}
    }
    onChange("");
  };

  return (
    <div
      className={`adm-upload-area ${drag ? "dragover" : ""}`}
      onClick={() => !uploading && inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFile(e.target.files[0])}
      />

      {value ? (
        <>
          <img src={value} alt="preview" className="adm-upload-preview" />
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>Click to replace</p>
          <button className="adm-upload-remove" onClick={handleRemove} title="Remove">×</button>
        </>
      ) : (
        <div className="adm-upload-label">
          <span className="icon">🖼️</span>
          <strong>Click to upload or drag & drop</strong>
          <span>Recommended: 1920×600px or wider</span>
        </div>
      )}

      {uploading && (
        <>
          <p style={{ color: "var(--cyan)", fontSize: 13, marginTop: 8 }}>Uploading... {progress}%</p>
          <div className="adm-upload-progress">
            <div className="adm-upload-progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function AdminHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchSlides = async () => {
    const snap = await getDocs(collection(db, "heroSlides"));
    setSlides(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };
  useEffect(() => { fetchSlides(); }, []);

  const handleAdd = async () => {
    if (!form.headline) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "heroSlides", editingId), { ...form });
        setEditingId(null);
      } else {
        await addDoc(collection(db, "heroSlides"), { ...form, createdAt: serverTimestamp() });
      }
      setForm(EMPTY);
      fetchSlides();
    } finally { setSaving(false); }
  };

  const handleEdit = (slide) => {
    setEditingId(slide.id);
    setForm({
      headline:   slide.headline   || "",
      subheading: slide.subheading || "",
      ctaLabel:   slide.ctaLabel   || "Shop Now",
      ctaLink:    slide.ctaLink    || "/products",
      imageUrl:   slide.imageUrl   || "",
      bgColor:    slide.bgColor    || "#0d0d1a",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const handleDelete = async (id, imageUrl) => {
    if (!window.confirm("Delete this slide?")) return;
    // Remove image from Storage too
    if (imageUrl) {
      try { await deleteObject(ref(storage, imageUrl)); } catch {}
    }
    await deleteDoc(doc(db, "heroSlides", id));
    fetchSlides();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* Add/Edit Slide Form */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">{editingId ? "Edit Hero Slide" : "Add Hero Slide"}</span>
        </div>
        <div style={{ padding: 22 }}>
          <div className="adm-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="adm-form-group">
                <label>Headline *</label>
                <input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
                  placeholder="Print That Makes Your Brand Stand Out" />
              </div>
              <div className="adm-form-group">
                <label>Sub-heading</label>
                <input value={form.subheading} onChange={e => setForm(f => ({ ...f, subheading: e.target.value }))}
                  placeholder="Premium quality printing..." />
              </div>
              <div className="adm-form-group">
                <label>CTA Button Label</label>
                <input value={form.ctaLabel} onChange={e => setForm(f => ({ ...f, ctaLabel: e.target.value }))} />
              </div>
              <div className="adm-form-group">
                <label>CTA Link</label>
                <input value={form.ctaLink} onChange={e => setForm(f => ({ ...f, ctaLink: e.target.value }))} />
              </div>
              <div className="adm-form-group">
                <label>Background Color <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400}}>(shown if no image)</span></label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={form.bgColor}
                    onChange={e => setForm(f => ({ ...f, bgColor: e.target.value }))}
                    style={{ width: 40, height: 40, padding: 2, borderRadius: 6, border: "1px solid rgba(255,255,255,0.15)", background: "none", cursor: "pointer" }} />
                  <input value={form.bgColor} onChange={e => setForm(f => ({ ...f, bgColor: e.target.value }))} style={{ flex: 1 }} />
                </div>
              </div>
            </div>

            {/* ── Hero Image Upload ── */}
            <div className="adm-form-group">
              <label>Background Image <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400}}>(optional — overlays on bg color)</span></label>
              <ImageUploader
                value={form.imageUrl}
                onChange={(url) => setForm(f => ({ ...f, imageUrl: url }))}
                folder="hero"
              />
            </div>

            {/* Live mini-preview */}
            {(form.headline || form.imageUrl) && (
              <div style={{
                borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)",
                background: form.bgColor || "#0d0d1a", position: "relative", minHeight: 80,
                backgroundImage: form.imageUrl ? `url(${form.imageUrl})` : "none",
                backgroundSize: "cover", backgroundPosition: "center"
              }}>
                <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)" }} />
                <div style={{ position: "relative", padding: "18px 20px" }}>
                  <p style={{ color: "white", fontWeight: 800, fontSize: 16, fontFamily: "var(--font-display)", textTransform: "uppercase" }}>{form.headline}</p>
                  {form.subheading && <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, marginTop: 4 }}>{form.subheading}</p>}
                  {form.ctaLabel && (
                    <div style={{ display: "inline-block", background: "var(--cyan)", color: "white", padding: "5px 14px", borderRadius: 4, fontSize: 12, fontWeight: 700, marginTop: 8 }}>
                      {form.ctaLabel}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button className="adm-btn adm-btn-primary" style={{ alignSelf: "flex-start" }}
                onClick={handleAdd} disabled={saving || !form.headline}>
                {saving ? "Saving..." : editingId ? "Update Slide" : "+ Add Slide"}
              </button>
              {editingId && (
                <button className="adm-btn" onClick={handleCancelEdit}
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)" }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slides list */}
      <div className="adm-card">
        <div className="adm-card-header">
          <span className="adm-card-title">Current Slides ({slides.length})</span>
        </div>
        {slides.length === 0 && (
          <p style={{ padding: 24, color: "rgba(255,255,255,0.3)", fontSize: 13 }}>
            No slides yet. The homepage will show a default hero.
          </p>
        )}
        {slides.map((s, i) => (
          <div key={s.id} style={{
            padding: "16px 22px", borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 72, height: 44, borderRadius: 6, flexShrink: 0,
                background: s.bgColor || "#0d0d1a",
                backgroundImage: s.imageUrl ? `url(${s.imageUrl})` : "none",
                backgroundSize: "cover", backgroundPosition: "center",
                border: "1px solid rgba(255,255,255,0.1)"
              }} />
              <div>
                <p style={{ color: "white", fontWeight: 600, fontSize: 14 }}>
                  <span style={{ color: "rgba(255,255,255,0.3)", marginRight: 8 }}>#{i + 1}</span>
                  {s.headline}
                </p>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 2 }}>{s.subheading}</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button className="adm-btn adm-btn-sm"
                onClick={() => handleEdit(s)}
                style={{ background: "rgba(0,170,221,0.12)", border: "1px solid var(--cyan)", color: "var(--cyan)" }}>
                Edit
              </button>
              <button className="adm-btn adm-btn-sm adm-btn-danger"
                onClick={() => handleDelete(s.id, s.imageUrl)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
